import { createMiddleware } from "@tanstack/react-start";
import { getContext } from "../../../api/src/site-context";
import { unpublishedAuthCreator } from "../../../api/src/creator-site-auth";
import { customSiteForCreator } from "../../../api/src/customization/custom-site";
import { requireCreatorTemplate } from "../../../api/src/feature-flags/catalog";
import { reportError, unexpectedError } from "../../../api/src/errors/report";
import { safeReturnPath } from "../../../api/src/routing";
import { managedSitePath } from "../../src/lib/managed-site-path";
import { hasCreatorEditingAccess } from "../../../api/src/service";

const noStore = { "Cache-Control": "private, no-store" };

/**
 * What anyone but its owner gets at a site that isn't published yet: a plain page that says so,
 * without the site's name, and for someone signed out the way into the owner's private view of
 * this page (a signed-in visitor who isn't its owner would only come back here).
 */
function unpublishedPage(signedIn: boolean) {
  // Query-only, so it opens this same page: a path written into the page could be read back as
  // another host once the browser decodes character references in it.
  const link = signedIn ? "" : `<a href="?preview=1">Owner sign-in</a>`;
  // On touch, the link answers a 44px tall target centred on it, as Drate's own links do.
  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="robots" content="noindex"><title>Not published yet</title><style>
:root{color-scheme:light dark;background:#fafaf9;color:#141413;font:16px/1.5 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif}
@media (prefers-color-scheme:dark){:root{background:#141413;color:#f5f5f4}}
body{margin:0;min-height:100vh;min-height:100dvh;display:grid;place-items:center;padding:24px;box-sizing:border-box;text-align:center}
h1{margin:0;font-size:20px;line-height:28px;font-weight:600;letter-spacing:-.01em}
a{display:inline-block;margin-top:8px;color:inherit;opacity:.64;font-size:14px;line-height:20px;text-underline-offset:3px;transition:opacity 160ms cubic-bezier(.22,1,.36,1)}
@media (hover:hover) and (pointer:fine){a:hover{opacity:1}}
@media (pointer:coarse){a{position:relative}a::after{content:"";position:absolute;inset:min(0px,calc(50% - 22px)) 0}}
</style></head><body><main><h1>This site isn’t published yet</h1>${link}</main></body></html>`;
  return new Response(html, {
    status: 404,
    headers: { ...noStore, "Content-Type": "text/html; charset=utf-8" },
  });
}

/**
 * The checks Astro's middleware (site/src/middleware.ts) makes before a creator page renders, for
 * every request Start answers: a page document or the route data a client navigation asks for.
 * The Worker already applied the site's host, password gate and API rules.
 *
 * Route data is always the published site: an owner's preview (?preview=1) loads whole documents,
 * so its draft never travels through client navigation or its in-memory route cache.
 */
export const siteGuard = createMiddleware({ type: "request" }).server(
  async ({ request, context, next, handlerType }) => {
    let drate;
    try {
      drate = await getContext(request, context.env, context.executionCtx);
    } catch (error) {
      if (error instanceof Error && error.message === "Unknown site")
        return new Response("Site not found", { status: 404, headers: noStore });
      throw error;
    }
    const creator = drate.creator;
    const url = new URL(request.url);
    // Drate's own pages on the platform host: the Worker sends only those (and their route data)
    // here, so there is no creator to check.
    if (!creator)
      try {
        return await next();
      } catch (error) {
        if (unexpectedError(error))
          reportError({ env: context.env, executionCtx: context.executionCtx }, error, {
            source: "page",
            route: url.pathname,
            method: request.method,
            userId: drate.user?.id,
          });
        throw error;
      }
    // The editor's previews were authorized when they were built (server/preview.server.ts) and
    // draw unpublished or unsaved work on purpose.
    if (drate.customizationPreview || drate.templatePreview || drate.publishedWebsitePreview)
      return next();
    const page = handlerType === "router";
    if (page && ["GET", "HEAD"].includes(request.method)) {
      const path = managedSitePath(url);
      if (path && (await customSiteForCreator(drate, creator.id))?.mode === "managed") {
        url.pathname = path;
        url.searchParams.delete("chapter");
        return new Response(null, { status: 302, headers: { Location: url.href, ...noStore } });
      }
    }
    // Signing in answers before the preview hand-off and template checks: an unpublished site
    // still lets its owner sign in, with its name only, and offers no new accounts.
    const account = page && ["/sign-in", "/sign-up"].includes(url.pathname);
    if (account && !creator.publishedConfig) {
      if (url.pathname === "/sign-up") {
        const destination = new URL("/sign-in", url);
        destination.searchParams.set(
          "returnTo",
          safeReturnPath(url.searchParams.get("returnTo") || "/?preview=1"),
        );
        return new Response(null, { status: 303, headers: { Location: destination.href } });
      }
      drate.creator = unpublishedAuthCreator(creator);
    }
    const preview = page && url.searchParams.get("preview") === "1";
    if (!account) {
      if (preview && !drate.user) {
        const handoff = new URL("/api/session/handoff", context.env.APP_ORIGIN);
        handoff.searchParams.set("target", request.url);
        return new Response(null, { status: 302, headers: { Location: handoff.href, ...noStore } });
      }
      if (preview && !(await hasCreatorEditingAccess(context.env, creator, drate.user))) {
        // Someone who doesn't edit a published site, following its owner's copied preview link
        // (a signed-in member, say), gets the live page.
        if (creator.publishedConfig) {
          const live = new URL(url);
          live.searchParams.delete("preview");
          return new Response(null, { status: 302, headers: { Location: live.href, ...noStore } });
        }
        return new Response("Site not found", { status: 404, headers: noStore });
      }
      // A published site's payment page needs no template check.
      if (!(page && url.pathname === "/checkout" && creator.publishedConfig))
        try {
          await requireCreatorTemplate(drate, creator.id, !preview, true);
        } catch {
          return new Response("Site not found", { status: 404, headers: noStore });
        }
      if (!creator.publishedConfig && !(preview && drate.user?.id === creator.ownerId)) {
        // Its owner, already signed in here, goes straight to their private view of this page.
        if (drate.user && drate.user.id === creator.ownerId) {
          const own = new URL(url);
          own.searchParams.set("preview", "1");
          return new Response(null, { status: 302, headers: { Location: own.href, ...noStore } });
        }
        return unpublishedPage(!!drate.user);
      }
    }
    try {
      return await next();
    } catch (error) {
      if (unexpectedError(error))
        reportError({ env: context.env, executionCtx: context.executionCtx }, error, {
          source: "page",
          route: url.pathname,
          method: request.method,
          creatorId: creator.id,
          userId: drate.user?.id,
        });
      throw error;
    }
  },
);
